import { MutationOf } from '~/services/trpc'

import { Timespan } from '@hap/utils'

import {
  LOAD_QUERY,
  SET_AGGREGATIONS,
  SET_COLUMNS,
  SET_FIELDS,
  SET_FILTERS,
  SET_ORDER,
  SET_ROWS,
  SET_TASK,
  SET_TIMESPAN,
} from './constants'
import { SavedQueryAction, SavedQueryState } from './types'

export function initializeReducer(opts: {
  qid: string
  mutator: MutationOf<'queries.upsert'>
  currentUserId?: string
}) {
  let timeout: number
  function reduce(state: SavedQueryState, action: SavedQueryAction): SavedQueryState {
    switch (action.type) {
      case LOAD_QUERY:
        return {
          meta: {
            isDirty: false,
            isSaved: action.payload.saved,
            isOwner: opts.currentUserId === action.payload.createdBy,
          },
          relationships: {
            task: action.payload.task || null,
          },
          data: {
            name: action.payload.name,
            description: action.payload.description,
            shared: action.payload.shared,
            saved: action.payload.saved,
            params: {
              timespan: Timespan.toStaticTimespan(action.payload.params.timespan),
              relativeTimespan: action.payload.params.timespan,
              filters: action.payload.params.filters,
              orderBy: action.payload.params.orderBy || [],
              rows: action.payload.params.rows || [],
              columns: action.payload.params.columns || [],
              fields: action.payload.params.fields || [],
              aggregations: action.payload.params.aggregations || [],
            },
          },
        }
      case SET_FILTERS:
        return {
          ...state,
          meta: {
            ...state.meta,
            isDirty: true,
          },
          data: {
            ...state.data,
            params: {
              ...state.data.params,
              filters: action.payload,
            },
          },
        }
      case SET_TIMESPAN:
        return {
          ...state,
          meta: {
            ...state.meta,
            isDirty: true,
          },
          data: {
            ...state.data,
            params: {
              ...state.data.params,
              timespan: Timespan.toStaticTimespan(action.payload),
              relativeTimespan: action.payload,
            },
          },
        }
      case SET_ORDER:
        return {
          ...state,
          data: {
            ...state.data,
            params: {
              ...state.data.params,
              orderBy: action.payload,
            },
          },
        }
      case SET_ROWS:
        return {
          ...state,
          data: {
            ...state.data,
            params: {
              ...state.data.params,
              rows: action.payload,
            },
          },
        }
      case SET_COLUMNS:
        return {
          ...state,
          meta: {
            ...state.meta,
            isDirty: true,
          },
          data: {
            ...state.data,
            params: {
              ...state.data.params,
              columns: action.payload,
            },
          },
        }
      case SET_AGGREGATIONS:
        return {
          ...state,
          data: {
            ...state.data,
            params: {
              ...state.data.params,
              aggregations: action.payload,
            },
          },
        }
      case SET_TASK:
        return {
          ...state,
          relationships: {
            task: action.payload,
          },
        }
      case SET_FIELDS:
        return {
          ...state,
          meta: {
            ...state.meta,
            isDirty: true,
          },
          data: {
            ...state.data,
            params: {
              ...state.data.params,
              fields: action.payload,
            },
          },
        }
      default:
        return state
    }
  }

  return (state: SavedQueryState, action: SavedQueryAction): SavedQueryState => {
    const nextState = reduce(state, action)
    if (action.type === LOAD_QUERY || nextState.meta.isSaved) return nextState
    clearTimeout(timeout)
    timeout = window.setTimeout(() => {
      opts.mutator
        .mutateAsync({
          id: opts.qid,
          params: {
            timespan: nextState.data.params.relativeTimespan,
            filters: nextState.data.params.filters,
            orderBy: nextState.data.params.orderBy,
            rows: nextState.data.params.rows,
            columns: nextState.data.params.columns,
            fields: nextState.data.params.fields,
            aggregations: nextState.data.params.aggregations,
          },
        })
        .catch(console.error)
    }, 1000)
    return nextState
  }
}
